import { Loading } from "../../app/Status";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { belowMinimum, formatQty, STOCK_MOVE_KIND_LABELS, type BuildingDto, type MaterialDto } from "@klantpunt/shared";
import { buildingPath } from "../../app/navItems";
import { IconArchive, IconClose, IconPencil, IconPlus } from "../../app/icons";
import { PageHeader } from "../../app/PageHeader";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useRealtime } from "../../lib/realtime";
import { useToast } from "../../lib/toast";
import { useBuilding } from "../building/useBuilding";
import { MaterialDialog, MoveDialog, priceLabel, StockDialog } from "./MaterialDialogs";
import { errMsg, useMaterials, useMaterialsRefresh, useStockMoves } from "./useMaterials";
import { companyPath } from "../directory/parts";

type Dialog =
  | { type: "new" }
  | { type: "edit"; m: MaterialDto }
  | { type: "move"; m: MaterialDto; kind: "received" | "correction" }
  | { type: "stock"; m: MaterialDto };

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const qtyUnit = (n: number, unit: string) => `${formatQty(n)} ${unit}`;

export function StockChip({ m }: { m: MaterialDto }) {
  if (!m.stock || m.stock.minQuantity == null) return null;
  return belowMinimum(m.stock) ? (
    <span className="chip dir-cert dir-cert-expired">Onder minimum</span>
  ) : (
    <span className="chip dir-cert dir-cert-ok">Op peil</span>
  );
}

/**
 * Materialen van een gebouw (docs/MATERIALEN.md, issue #173): de artikelen van de klant en van het gebouw, met
 * de reservevoorraad in het gebouw, minimum, plek en mutaties.
 */
export function MaterialsPage() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const sel = params.get("artikel");
  const [archived, setArchived] = useState(false);
  const [onlyLow, setOnlyLow] = useState(false);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { data: b } = useBuilding(id);
  const list = useMaterials(id, { archived });
  useRealtime(id);
  const canEdit = hasModule(me, "materials", "edit");

  const select = (mid: string | null) => {
    const p = new URLSearchParams(params);
    if (mid) p.set("artikel", mid);
    else p.delete("artikel");
    setParams(p, { replace: true });
  };

  if (!hasModule(me, "materials"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Materialen.</p>
      </div>
    );
  if (!b || !list.data) return <div className="center">{list.isError ? <p>{errMsg(list.error)}</p> : <Loading />}</div>;

  const all = list.data.filter((m) => archived || !m.archived);
  const low = all.filter((m) => belowMinimum(m.stock));
  const needle = q.trim().toLowerCase();
  const rows = all.filter(
    (m) =>
      (!onlyLow || belowMinimum(m.stock)) &&
      (!needle || [m.name, m.articleNo, m.supplier?.name, m.stock?.location].join(" ").toLowerCase().includes(needle)),
  );
  const current = sel ? (list.data.find((m) => m.id === sel) ?? null) : null;

  return (
    <div className={`page dir-page mat-page ${current ? "has-sel" : ""}`}>
      <PageHeader
        label={
          <>
            Materialen · <span className="code">{b.code}</span>
          </>
        }
        title={b.name}
        action={
          canEdit && (
            <button type="button" className="btn primary" onClick={() => setDialog({ type: "new" })}>
              <IconPlus size={20} aria-hidden="true" /> Artikel
            </button>
          )
        }
      />
      <div className="dir-filters">
        <input
          type="search"
          className="dir-search"
          aria-label="Zoeken in materialen"
          placeholder="Zoek op naam, nummer of leverancier"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="dir-kinds" role="group" aria-label="Filter">
          <button
            type="button"
            className="kpi"
            aria-pressed={!onlyLow && !archived}
            onClick={() => (setOnlyLow(false), setArchived(false))}
          >
            Alles <span className="count num">{all.filter((m) => !m.archived).length}</span>
          </button>
          {low.length > 0 && (
            <button type="button" className="kpi doc-attn" aria-pressed={onlyLow} onClick={() => setOnlyLow((v) => !v)}>
              Onder minimum <span className="count num">{low.length}</span>
            </button>
          )}
          <button type="button" className="kpi" aria-pressed={archived} onClick={() => setArchived((v) => !v)}>
            Met gearchiveerde
          </button>
        </div>
      </div>
      <div className="dir-layout">
        <div className="dir-list tscroll">
          <table className="it" aria-label="Materialen">
            <thead>
              <tr>
                <th scope="col">Artikel</th>
                <th scope="col" className="num">
                  Voorraad
                </th>
                <th scope="col" className="num dir-hide-narrow">
                  Minimum
                </th>
                <th scope="col" className="dir-hide-narrow">
                  Plek
                </th>
                <th scope="col" className="dir-hide-narrow">
                  Prijs
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} aria-current={sel === m.id} onClick={() => select(m.id)}>
                  <td className="tcell-title">
                    <button type="button" className="linkbtn" onClick={() => select(m.id)}>
                      {m.name}
                    </button>
                    <div className="muted small">
                      {[m.articleNo, m.supplier?.name, m.scope === "building" ? "alleen dit gebouw" : null]
                        .filter(Boolean)
                        .join(" · ")}
                      {m.archived && " · gearchiveerd"}
                    </div>
                  </td>
                  <td className="num">
                    {qtyUnit(m.stock?.quantity ?? 0, m.unit)}
                    <div>
                      <StockChip m={m} />
                    </div>
                  </td>
                  <td className="num dir-hide-narrow">
                    {m.stock?.minQuantity != null ? formatQty(m.stock.minQuantity) : "–"}
                  </td>
                  <td className="dir-hide-narrow dir-wrap small">{m.stock?.location ?? "–"}</td>
                  <td className="dir-hide-narrow small">{priceLabel(m)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <p className="muted">
              {all.length
                ? "Geen artikelen gevonden."
                : "Nog geen artikelen. Voeg filters, V-snaren of andere reserveonderdelen toe die je bij onderhoud gebruikt."}
            </p>
          )}
        </div>
        {current && (
          <MaterialPanel
            key={current.id}
            building={b}
            m={current}
            canEdit={canEdit}
            onClose={() => select(null)}
            onDialog={setDialog}
          />
        )}
      </div>

      {dialog?.type === "new" && (
        <MaterialDialog building={b} onClose={() => setDialog(null)} onSaved={(mid) => select(mid)} />
      )}
      {dialog?.type === "edit" && <MaterialDialog building={b} material={dialog.m} onClose={() => setDialog(null)} />}
      {dialog?.type === "move" && (
        <MoveDialog buildingId={b.id} material={dialog.m} kind={dialog.kind} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "stock" && (
        <StockDialog buildingId={b.id} material={dialog.m} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function MaterialPanel({
  building: b,
  m,
  canEdit,
  onClose,
  onDialog,
}: {
  building: BuildingDto;
  m: MaterialDto;
  canEdit: boolean;
  onClose: () => void;
  onDialog: (d: Dialog) => void;
}) {
  const toast = useToast();
  const refresh = useMaterialsRefresh(b.id);
  const moves = useStockMoves(b.id, m.id);
  const archive = async () => {
    try {
      await api(`/buildings/${b.id}/materials/${m.id}`, { method: "PATCH", body: { archived: !m.archived } });
      await refresh();
      toast(m.archived ? "Artikel teruggezet" : "Artikel gearchiveerd");
    } catch (e) {
      toast(errMsg(e));
    }
  };
  return (
    <aside className="dir-panel" aria-label={m.name}>
      <header className="dir-panelhead">
        <div className="grow">
          <h2>{m.name}</h2>
          <div className="dir-sub">
            {m.scope === "client" ? `Alle gebouwen van ${b.client.name}` : `Alleen ${b.name}`}
            {m.archived && " · gearchiveerd"}
          </div>
        </div>
        {canEdit && (
          <button type="button" className="btn small ghost" onClick={() => onDialog({ type: "edit", m })}>
            <IconPencil aria-hidden="true" /> Bewerken
          </button>
        )}
        <button type="button" className="btn ghost small ico" onClick={onClose} aria-label="Paneel sluiten">
          <IconClose aria-hidden="true" />
        </button>
      </header>
      <section className="dir-sec">
        <h3>Voorraad in dit gebouw</h3>
        <p className="mat-stock">
          <strong className="num">{qtyUnit(m.stock?.quantity ?? 0, m.unit)}</strong> <StockChip m={m} />
        </p>
        <dl className="kv">
          <dt>Minimum</dt>
          <dd>{m.stock?.minQuantity != null ? qtyUnit(m.stock.minQuantity, m.unit) : "Geen"}</dd>
          <dt>Plek</dt>
          <dd>{m.stock?.location ?? "Nog niet ingevuld"}</dd>
        </dl>
        {canEdit && (
          <div className="btnrow">
            <button
              type="button"
              className="btn small primary"
              onClick={() => onDialog({ type: "move", m, kind: "received" })}
            >
              Ontvangen
            </button>
            <button
              type="button"
              className="btn small"
              onClick={() => onDialog({ type: "move", m, kind: "correction" })}
            >
              Tellen
            </button>
            <button type="button" className="btn small ghost" onClick={() => onDialog({ type: "stock", m })}>
              Minimum en plek
            </button>
          </div>
        )}
      </section>
      <section className="dir-sec">
        <h3>Gegevens</h3>
        <dl className="kv">
          {m.articleNo && (
            <>
              <dt>Artikelnummer</dt>
              <dd className="code">{m.articleNo}</dd>
            </>
          )}
          <dt>Eenheid</dt>
          <dd>{m.unit}</dd>
          <dt>Prijs</dt>
          <dd>{priceLabel(m)}</dd>
          <dt>Leverancier</dt>
          <dd>{m.supplier ? <Link to={companyPath(m.supplier.id)}>{m.supplier.name}</Link> : "–"}</dd>
          {m.notes && (
            <>
              <dt>Notities</dt>
              <dd className="dir-notes">{m.notes}</dd>
            </>
          )}
        </dl>
      </section>
      <section className="dir-sec">
        <h3>Gebruikt bij onderhoud</h3>
        {m.usedBy.length ? (
          <ul className="dir-items">
            {m.usedBy.map((u) => (
              <li key={u.obligationId}>
                <Link
                  className="grow"
                  to={`${buildingPath(b.id, "keuringen")}${u.assetId ? `?asset=${u.assetId}` : "?gebouw=1"}`}
                >
                  <span className="code">{u.assetCode ?? "Gebouw"}</span> {u.obligationName}
                </Link>
                <span className="num small">{qtyUnit(u.quantity, m.unit)} per keer</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Nog niet gekoppeld. Voeg het toe bij een onderhoudsbeurt van een asset.</p>
        )}
      </section>
      <section className="dir-sec">
        <h3>Mutaties</h3>
        {moves.data?.length ? (
          <ul className="dir-items">
            {moves.data.map((x) => (
              <li key={x.id}>
                <span className="grow">
                  {STOCK_MOVE_KIND_LABELS[x.kind]}
                  {x.task && (
                    <>
                      {" "}
                      · <span className="code">{x.task.code}</span>
                    </>
                  )}
                  <span className="muted small block">
                    {fmtWhen(x.createdAt)}
                    {x.by && ` · ${x.by}`}
                    {x.note && ` · ${x.note}`}
                  </span>
                </span>
                <span className="num">
                  {x.delta > 0 ? "+" : ""}
                  {formatQty(x.delta)}
                  <span className="muted small block">stand {formatQty(x.balance)}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : moves.isPending ? (
          <Loading small />
        ) : (
          <p className="muted small">Nog geen mutaties.</p>
        )}
      </section>
      {canEdit && (
        <section className="dir-sec">
          <button type="button" className="btn small ghost" onClick={() => void archive()}>
            <IconArchive size={18} aria-hidden="true" /> {m.archived ? "Terugzetten" : "Archiveren"}
          </button>
        </section>
      )}
    </aside>
  );
}
